<template>
  <div :class="$b({ disable360, dragging })">
    <ProgressSpinner
      v-show="loadPercentage !== 100"
      :percentage="loadPercentage"
    />

    <ThreeSixtyCanvas
      ref="canvas"
      :show-background="showBackground"
      :show-reflections="showReflections"
      :full-size="fullSize"
      :show-secondary="customisedProduct.conversionKitEnabled"
      :secondary-minimised="secondaryMinimised"
      :show-iris-preview="showIrisPreview"
    />

    <DynamicImg
      :class="$b('static-image', { hidden: canvasReady })"
      :sku="customisedProduct.legacySKU"
      :width="1400"
      :height="1000"
      :crop-factor="2100"
      :view-angle="viewAngle"
      bg="transparent"
      no-crop-adjust
    />
    
    <div
      v-if="showControls"
      :class="$b('controls')"
    >
      <p>
        Frame Number: {{ frameNumber }}
      </p>
      <p>
        Animate on tab change:
        <input
          v-model="animateOnChange"
          type="checkbox"
        >
      </p>
      <p>
        Show Background:
        <input
          v-model="showBackground"
          type="checkbox"
        >
      </p>
      <p>
        Show Reflections:
        <input
          v-model="showReflections"
          type="checkbox"
        >
      </p>
      <p>
        Full Size Product:
        <input
          v-model="fullSize"
          type="checkbox"
        >
      </p>
      <div>
        Frames to load:
        <ul>
          <li
            v-for="([frame, left]) in loadState"
            :key="frame"
          >
            {{ frame }}: {{ left }}
          </li>
        </ul>
      </div>
      <p>
        Correct Scale and Offset:
        <input
          v-model="enableScaleAndOffset"
          type="checkbox"
        >
      </p>
      <p>
        Scale:
        <input
          v-model="scale"
          type="number"
        />
      </p>
      <p>
        X Offset:
        <input
          v-model="xOffset"
          type="number"
        />
      </p>
      <p>
        Y Offset:
        <input
          v-model="yOffset"
          type="number"
        />
      </p>

      <LineLink
        is-button
        size="inherit"
        @click="() => exportFrame(false, false)"
      >
        Export
      </LineLink>

      <LineLink
        is-button
        size="inherit"
        @click="exportFrame(true, false)"
      >
        Export w/ Reflections
      </LineLink>

      <LineLink
        is-button
        size="inherit"
        @click="exportFrame(true, true)"
      >
        Export w/ Reflections &amp; BG
      </LineLink>
    </div>
  </div>
</template>

<script>
import AnimationMixin from './ThreeSixtyAnimation'
import MouseInputMixin from './ThreeSixtyMouseInput'
import KeyboardInputMixin from './ThreeSixtyKeyboardInput'
import CustomisedProduct from '~/models/CustomisedProduct'
import { debounce } from '~/assets/js/helpers'

import mitt from 'mitt'

import Layer from '~/models/Layer'

const keyframes = [{
  frame: 1,
  scale: 120,
  xOffset: 50,
  yOffset: -60
}, {
  frame: 14,
  scale: 120,
  xOffset: 0,
  yOffset: -60
}, {
  frame: 26,
  scale: 120,
  xOffset: 120,
  yOffset: -130
}, {
  frame: 41,
  scale: 120,
  xOffset: 0,
  yOffset: -40
}, {
  frame: 49,
  scale: 120,
  xOffset: 0,
  yOffset: -60
}, {
  frame: 58,
  scale: 120,
  xOffset: 0,
  yOffset: -40
}]

const noop = () => {}

export default {
  name: 'ThreeSixty',
  mixins: [
    AnimationMixin,
    MouseInputMixin,
    KeyboardInputMixin
  ],

  props: {
    layerTemplate: { type: Object, required: true },
    customisedProduct: { type: CustomisedProduct, required: true },
    showIrisPreview: { type: Boolean, default: false },
    frameNumberOverride: { type: Number, default: null },
    viewAngle: { type: Number, default: null },
    initOnMount: { type: Boolean, default: false }
  },

  emits: ['frame-change'],

  setup () {
    const { isMdBreakpointOrAbove } = useBreakpoint()
    
    return {
      isMdBreakpointOrAbove
    }
  },

  data: () => ({
    localEventBus: null,
    layers: [],
    frameCount: 72,
    frameNumber: 1,
    showBackground: false,
    showReflections: false,
    fullSize: false,
    showControls: false,
    scale: 100,
    xOffset: 0,
    yOffset: 0,
    keyframes,
    enableScaleAndOffset: false,
    animateOnChange: false,
    selectedOptions: {},
    selectedSecondaryOptions: {},
    keyAngles: [
      1, // Hero
      14, // Front on
      33, // Classic / Icons
      43, // Side / Earsocks
      49, // Behind
      58 // Side
    ],
    canvasReady: false,
    loadPercentage: 0,
    initialisedWithSKU: null,
    refreshCounter: 0
  }),

  computed: {
    loadState () {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.refreshCounter // Force reactive update
      return this.layers.map(layer => [layer.name, layer.numberFramesLeftToLoad])
    },
    timePerFrame () {
      return 1000 / this.frameRate
    },
    secondaryMinimised () {
      return this.frameNumber > 3 && this.frameNumber < 68
    },
    currentFrameIds () {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.refreshCounter // Force reactive update

      const optionFrameIds = Object.entries(this.selectedOptions).map(([layerId, selectedOption]) => {
        if (selectedOption === 'none') return null
        return `${layerId}:${selectedOption || 'default'}:${this.frameNumber}`
      }).filter(frameId => frameId)

      const nonOptionFrameIds = this.layers.filter(
        layer => !layer.hasOptions
      ).map(
        layer => `${layer.id}:default:${this.frameNumber}`
      )

      return [...optionFrameIds, ...nonOptionFrameIds]
    },
    disable360 () {
      return this.loadPercentage < 100
    },
    currentDesignTotalFrameCount () {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.refreshCounter // Force reactive update
      return this.layers.reduce((total, layer) => total + layer.currentDesignFrameCount, 0)
    },
    currentDesignFramesLoaded () {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.refreshCounter // Force reactive update
      return this.layers.reduce((total, layer) => total + layer.currentDesignFramesLoaded, 0)
    },
  },

  watch: {
    frameNumber () {
      this.frameNumber = this.getFrameNumber(this.frameNumber)
      this.layers.forEach((layer) => {
        if (layer.isSecondary && this.secondaryMinimised) return
        layer.frameNumber = this.frameNumber
      })
      this.render()
      this.preload()

      this.$emit('frame-change', this.frameNumber)
    },
    frameNumberOverride () {
      this.frameNumber = this.frameNumberOverride
    },
    async 'customisedProduct.product.sku' () {
      await this.init()
    },
    'customisedProduct.sku' () {
      this.selectedOptions = this.layerTemplate.resolveOptions(this.customisedProduct)
    },
    'customisedProduct.secondarySKU' () {
      this.selectedSecondaryOptions = (this.layerTemplate.resolveSecondaryOptions || noop)(this.customisedProduct) || {}
    },
    'customisedProduct.frameType' () {
      this.selectedOptions = this.layerTemplate.resolveOptions(this.customisedProduct)
      this.selectedSecondaryOptions = (this.layerTemplate.resolveSecondaryOptions || noop)(this.customisedProduct) || {}
    },
    selectedOptions () {
      this.handleSelectionChange()
    },
    selectedSecondaryOptions () {
      this.handleSelectionChange()
    },
    scale () {
      this.render()
    },
    xOffset () {
      this.render()
    },
    yOffset () {
      this.render()
    }
  },

  mounted () {
    if (this.frameNumberOverride) this.frameNumber = this.frameNumberOverride
    this.handleSelectionChange = this.throttleFunction(this.handleSelectionChange, 5)

    this.preload = debounce(this.preload, 250)

    window.show360Controls = () => {
      this.showControls = true
    }

    if (this.initOnMount) {
      this.init()
    }
  },

  beforeUnmount () {
    window.show360Controls = () => {}

    this.localEventBus?.off('load', this.handleFrameLoad)
    this.localEventBus?.off('update-load-state', this.handleUpdateLoadState)
  },

  methods: {
    async init () {
      if (this.initialisedWithSKU === this.customisedProduct.sku) return

      this.localEventBus = mitt()

      this.localEventBus.on('load', this.handleFrameLoad)
      this.localEventBus.on('update-load-state', this.handleUpdateLoadState)

      this.selectedOptions = this.layerTemplate.resolveOptions(this.customisedProduct)
      this.selectedSecondaryOptions = (this.layerTemplate.resolveSecondaryOptions || noop)(this.customisedProduct) || {}

      await this.initLayers()
      this.render()

      this.preload()
      
      this.canvasReady = true
      this.initialisedWithSKU = this.customisedProduct.sku

      // Periodically get updated frame counts from each layer
      setInterval(() => {
        this.layers.forEach((layer) => {
          if (layer.isSecondary && this.secondaryMinimised) return
          layer.updateFrameCounts()
        })
      }, 1000)
    },
    async initLayers () {
      const getSelectedOption = (layerId) => {
        if (layerId in this.selectedOptions && this.selectedOptions[layerId] !== 'none') return this.selectedOptions[layerId]
        else if (layerId in this.selectedSecondaryOptions && this.selectedSecondaryOptions[layerId] !== 'none') return this.selectedSecondaryOptions[layerId]
        return null
      }

      const validLayers = this.layerTemplate.layers.filter(layer => !layer.isReflection && !layer.isBackground)

      this.layers = await Promise.all(validLayers.map(layer => new Layer({
        ...layer,
        selectedOption: getSelectedOption(layer.id),
        isSecondary: layer.id in this.selectedSecondaryOptions && this.selectedSecondaryOptions[layer.id] !== 'none',
        frameCount: this.frameCount,
        frameNumber: this.frameNumber,
        useMobileImages: !this.isMdBreakpointOrAbove,
        keyAngles: this.keyAngles,
        eventBus: this.localEventBus
      })))

      this.refreshCounter += 1
    },
    async preload () {
      await Promise.all(this.layers.filter(layer => !layer.isBackground).map(layer => layer.preload()))
    },
    async preloadAll () {
      await Promise.all(this.layers.map(layer => layer.preloadAll()))
    },
    handleFrameLoad (frameId) {
      if (this.currentFrameIds.includes(frameId)) requestAnimationFrame(this.render)
    },
    handleUpdateLoadState () {
      this.refreshCounter += 1

      if (!this.currentDesignTotalFrameCount) return
      
      this.loadPercentage = 100 * this.currentDesignFramesLoaded / this.currentDesignTotalFrameCount

      if (this.loadPercentage === 100) this.initialisedWithSKU = this.customisedProduct.sku
    },
    handleSelectionChange () {
      const validLayerIds = this.layers.map(layer => layer.id)

      Object.entries(this.selectedOptions).forEach(([id, option]) => {
        if (!validLayerIds.includes(id)) return
        if (!option) console.error(`No option selected for layer '${id}'`)

        const layer = this.getLayerByID(id)
        if (layer) {
          layer.setSelectedOption(option)

          // If layer was previously secondary then update the frameNumber
          if (layer.isSecondary) layer.frameNumber = this.frameNumber

          layer.isSecondary = false
        }
      })

      Object.entries(this.selectedSecondaryOptions).forEach(([id, option]) => {
        if (!validLayerIds.includes(id)) return
        if (!option) console.error(`No option selected for layer '${id}'`)

        if (option === 'none') return
        
        const layer = this.getLayerByID(id)
        if (layer) {
          layer.selectedOption = option

          // If layer was previously primary then update the frameNumer
          if (!layer.isSecondary) layer.frameNumber = 1
          
          layer.isSecondary = true
        }
      })

      this.render()
      this.preload()
    },
    computeScaleAndOffset () {
      if (!this.enableScaleAndOffset) return { scale: 100, xOffset: 0, yOffset: 0 }
      
      // Find first keyframe after this frame
      let nextKeyframeIndex = this.keyframes.findIndex(keyframe => keyframe.frame > this.frameNumber)
      if (nextKeyframeIndex === -1) nextKeyframeIndex = 0
      const nextKeyframe = this.keyframes[nextKeyframeIndex]

      // Get the previous keyframe
      let previousKeyframeIndex = nextKeyframeIndex - 1
      if (previousKeyframeIndex === -1) previousKeyframeIndex = this.keyframes.length - 1
      const previousKeyframe = this.keyframes[previousKeyframeIndex]

      // Determine the distance between the two
      let backwardsDistance = this.frameNumber - previousKeyframe.frame
      if (backwardsDistance < 0) backwardsDistance += this.frameCount - 1
      let forwardsDistance = nextKeyframe.frame - this.frameNumber
      if (forwardsDistance < 0) forwardsDistance += this.frameCount - 1
      const totalDistance = backwardsDistance + forwardsDistance
      const scaler = backwardsDistance / totalDistance

      // Calculate scale, xOffset and yOffset
      const getScaledValue = (next, previous, scaler) => {
        return previous + (next - previous) * scaler
      }
      
      return {
        scale: getScaledValue(nextKeyframe.scale, previousKeyframe.scale, scaler),
        xOffset: getScaledValue(nextKeyframe.xOffset, previousKeyframe.xOffset, scaler),
        yOffset: getScaledValue(nextKeyframe.yOffset, previousKeyframe.yOffset, scaler)
      }
    },
    render () {
      const scaleAndOffset = this.computeScaleAndOffset()
      this.scale = scaleAndOffset.scale
      this.xOffset = scaleAndOffset.xOffset
      this.yOffset = scaleAndOffset.yOffset

      this.$refs.canvas?.render(this.layers, this.scale, this.xOffset, this.yOffset)
    },
    getFrameNumber (frameNumber) {
      while (frameNumber > this.frameCount) { frameNumber -= this.frameCount }
      while (frameNumber <= 0) { frameNumber += this.frameCount }
      return frameNumber
    },
    getLayerByID (id) {
      return this.layers.find(layer => layer.id === id)
    },
    animateToIfEnabled (targetFrame) {
      if (this.animateOnChange) this.animateTo(targetFrame)
    },
    animatePrev () {
      const targetFrame = this.keyAngles.filter(frame => frame > this.frameNumber)?.[0] || this.keyAngles[0]
      this.animateTo(targetFrame)
    },
    animateNext () {
      const reversedKeyAngles = [...this.keyAngles].reverse()
      const targetFrame = reversedKeyAngles.filter(frame => frame < this.frameNumber)?.[0] || reversedKeyAngles[0]
      this.animateTo(targetFrame)
    },
    exportFrame (includeReflections = false, includeBG = false) {
      const srcs = this.layers.filter((layer) => {
        if (layer.isBackground && !includeBG) return false
        if (layer.isReflection && !includeReflections) return false
        return layer.img?.src
      }).map(
        layer => layer.img.src.split('gs://sg360/')[1]
      )
      window.open(`https://sg360-stack-cf.sungod.co/?paths=${srcs.join(',')}`)
    }
  }
}
</script>

<style lang="postcss">
.ThreeSixty {
  @apply
    relative
    mdMax:h-full
    cursor-grab;

  &--dragging {
    @apply cursor-grabbing;
  }

  &--disable360 {
    @apply cursor-default
      h-full;
  }
}

.ThreeSixty__toggle {
  @apply absolute bottom-xs left-xs;
}

.ThreeSixty__controls {
  @apply mb-sm
    absolute
    bottom-xs right-xs
    bg-white
    text-xxs;
  
  z-index: 50;
}

.ThreeSixty__static-image {
  @apply absolute top-0 left-0
    h-full w-full
    object-contain
    pointer-events-none
    opacity-100 transition-opacity
    mdMax:pb-sm;

  &--hidden {
    @apply opacity-0;
  }
}
</style>